const REF_SCHEME = "attachment:";

const MARKDOWN_IMAGE = /!\[([^\]]*)\]\(\s*([^)\s]+)([^)]*)\)/g;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function attachmentRef(id: string): string {
  return `${REF_SCHEME}${id}`;
}

export function refAttachmentId(src: string): string | null {
  if (!src.startsWith(REF_SCHEME)) return null;
  const id = src.slice(REF_SCHEME.length);
  return UUID.test(id) ? id : null;
}

export function inlineImageIds(markdown: string): string[] {
  const ids = new Set<string>();
  for (const [, , src] of markdown.matchAll(MARKDOWN_IMAGE)) {
    const id = refAttachmentId(src);
    if (id) ids.add(id);
  }
  return Array.from(ids);
}

export function inflateInlineImages(markdown: string, urlById: Map<string, string>): string {
  return markdown.replace(MARKDOWN_IMAGE, (whole, alt: string, src: string, tail: string) => {
    const id = refAttachmentId(src);
    if (!id) return whole;
    const url = urlById.get(id);
    return url ? `![${alt}](${url}${tail})` : whole;
  });
}

export function resolveInlineImages(markdown: string, urlById: Map<string, string>): string {
  const index = attachmentIdIndex(invertUrlById(urlById));
  return markdown.replace(MARKDOWN_IMAGE, (_whole, alt: string, src: string, tail: string) => {
    const id = refAttachmentId(src) ?? attachmentIdOf(index, src);
    const url = id ? urlById.get(id) : undefined;
    return url ? `![${alt}](${url}${tail})` : alt;
  });
}

export function attachmentUrlKey(url: string): string {
  const cut = url.search(/[?#]/);
  return cut === -1 ? url : url.slice(0, cut);
}

export function attachmentIdIndex(idByUrl: Map<string, string>): Map<string, string> {
  const index = new Map<string, string>();
  for (const [url, id] of idByUrl) {
    index.set(url, id);
    index.set(attachmentUrlKey(url), id);
  }
  return index;
}

export function attachmentIdOf(index: Map<string, string>, src: string): string | null {
  return index.get(src) ?? index.get(attachmentUrlKey(src)) ?? null;
}

export const URL_REFRESH_MARGIN_MS = 5 * 60 * 1000;

const SIGNED_DATE = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/;

export function signedUrlExpiry(url: string): number | null {
  const cut = url.indexOf("?");
  if (cut === -1) return null;
  const params = new URLSearchParams(url.slice(cut + 1));
  const signedAt = SIGNED_DATE.exec(params.get("X-Amz-Date") ?? "");
  const lifetime = Number(params.get("X-Amz-Expires"));
  if (!signedAt || !Number.isFinite(lifetime) || lifetime <= 0) return null;
  const [, year, month, day, hour, minute, second] = signedAt;
  return (
    Date.UTC(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second)) +
    lifetime * 1000
  );
}

export function anyUrlExpiresBefore(urls: Iterable<string>, deadline: number): boolean {
  for (const url of urls) {
    const expiry = signedUrlExpiry(url);
    if (expiry !== null && expiry <= deadline) return true;
  }
  return false;
}

export function invertUrlById(urlById: Map<string, string>): Map<string, string> {
  return new Map(Array.from(urlById, ([id, url]) => [url, id]));
}

export function refreshInlineImages(
  markdown: string,
  urlById: Map<string, string>,
  deadline: number,
): string {
  const index = attachmentIdIndex(invertUrlById(urlById));
  return markdown.replace(MARKDOWN_IMAGE, (whole, alt: string, src: string, tail: string) => {
    const id = refAttachmentId(src) ?? attachmentIdOf(index, src);
    const url = id ? urlById.get(id) : undefined;
    if (!url || url === src) return whole;
    const expiry = signedUrlExpiry(src);
    if (expiry !== null && expiry > deadline) return whole;
    return `![${alt}](${url}${tail})`;
  });
}

export function deflateInlineImages(markdown: string, idByUrl: Map<string, string>): string {
  const index = attachmentIdIndex(idByUrl);
  return markdown.replace(MARKDOWN_IMAGE, (whole, alt: string, src: string, tail: string) => {
    if (refAttachmentId(src)) return whole;
    const id = attachmentIdOf(index, src);
    return id ? `![${alt}](${attachmentRef(id)}${tail})` : whole;
  });
}
